import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Autocomplete,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { gsfService } from '../services/gsfService';

// GSF Logo SVG Component
const GSFLogo = ({ size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="none"
    viewBox="0 0 330 330"
  >
    <clipPath id="gsfClip">
      <path fill="#fff" d="M0 0h330v330H0z" />
    </clipPath>
    <g clipPath="url(#gsfClip)">
      <path d="M325.461 4.536H4.532v320.928h320.929V4.536z" fill="#ed1c24" />
      <path d="M325.352 4.644H4.639v320.713h320.713V4.644z" stroke="#fff" strokeWidth="5.547" />
      <path d="M78.54 231.815a20.81 20.81 0 0 1-7.459 11.582c-3.729 2.901-8.335 4.447-13.06 4.383-2.824.099-5.638-.37-8.277-1.381a21.08 21.08 0 0 1-11.846-11.407 21.08 21.08 0 0 1-1.692-8.219c-.007-2.825.553-5.623 1.649-8.227s2.704-4.962 4.728-6.932 4.425-3.514 7.058-4.538 5.445-1.508 8.269-1.424a20.77 20.77 0 0 1 13.189 4.419c3.758 2.946 6.385 7.097 7.44 11.753H67.675c-.709-2.047-2.033-3.825-3.79-5.092s-3.863-1.959-6.03-1.984c-6.966 0-10.856 6.092-10.856 11.852 0 6.141 4.166 12.064 10.912 12.064 2.121-.06 4.177-.742 5.913-1.962a10.82 10.82 0 0 0 3.851-4.896l10.865.009z" fill="#fff" />
      <path d="M158.193 130.009c.157 2.069.157 3.825.157 6.213 0 39.504-30.268 63.242-62.916 63.242a61.63 61.63 0 0 1-23.925-4.428 61.65 61.65 0 0 1-20.347-13.341 61.64 61.64 0 0 1-18.332-44.043c0-35.2 28.355-60.682 63.077-60.682 28.194 0 52.41 18.95 58.941 41.73h-33.772c-2.501-4.69-6.248-8.599-10.829-11.296a28.88 28.88 0 0 0-15.131-3.99c-14.815 0-32.177 10.986-32.177 34.238 0 24.698 17.519 35.368 32.33 35.368 14.977 0 25.01-7.488 28.203-18.956h-35.2v-24.055h69.922z" fill="#fff" />
      <path d="M201.048 113.607c-.634-3.982-1.596-11.471-10.828-11.471-5.256 0-10.356 3.664-10.356 9.237 0 7.009 3.186 8.442 24.373 17.999 21.971 9.881 27.552 20.071 27.552 33.772 0 17.207-9.865 36.159-41.099 36.159-34.078 0-42.524-22.301-42.524-37.442v-3.816h29.944c0 13.697 8.446 16.085 12.266 16.085 1.496.013 2.98-.269 4.367-.83s2.649-1.39 3.714-2.44 1.913-2.3 2.494-3.679a11.34 11.34 0 0 0 .893-4.353c0-7.811-4.932-9.715-23.092-17.365-8.296-3.34-28.837-11.629-28.837-33.771S171.42 76.97 191.168 76.97c11.625 0 24.214 4.293 31.861 13.374 7.004 8.6 7.482 16.569 7.8 23.261l-29.781.003z" fill="#fff" />
      <path d="M232.505 80.22h62.862v25.828h-34.702v19.602h33.455v25.826h-33.455v43.883h-28.16V80.22z" fill="#fff" />
    </g>
  </svg>
);

/**
 * Inline GSF Parts Search Component
 *
 * @param {boolean} expanded - Whether the search section is expanded
 * @param {function} onToggle - Called when expand/collapse button clicked
 * @param {function} onSelect - Called with {sku, price, description, brand} when part selected
 * @param {string} vehicleRegistration - Pre-filled vehicle registration
 * @param {string} itemName - Item name for suggesting relevant part types
 * @param {boolean} disabled - Disable the search (e.g., for paid items)
 */
const GSFInlineSearch = ({
  expanded,
  onToggle,
  onSelect,
  vehicleRegistration,
  itemName,
  disabled = false,
}) => {
  // State
  const [error, setError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [partTypes, setPartTypes] = useState([]);
  const [partTypesLoading, setPartTypesLoading] = useState(false);

  const [registration, setRegistration] = useState('');
  const [selectedPartType, setSelectedPartType] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Load part types when expanded
  useEffect(() => {
    const loadPartTypes = async () => {
      if (partTypes.length > 0) return; // Already loaded

      setPartTypesLoading(true);
      try {
        const data = await gsfService.getPartTypes();
        setPartTypes(data.part_types || []);
      } catch (err) {
        console.error('Failed to load part types:', err);
        setError('Failed to load part types. Please try again.');
      } finally {
        setPartTypesLoading(false);
      }
    };

    if (expanded) {
      loadPartTypes();
      setRegistration(vehicleRegistration || '');
      setSearchResults([]);
      setHasSearched(false);
      setError(null);
      setSelectedPartType(null);
    }
  }, [expanded, vehicleRegistration, partTypes.length]);

  // Filter part types based on item name to suggest relevant types
  const suggestedPartTypes = useMemo(() => {
    if (!itemName || !partTypes.length) return partTypes;

    const lowerName = itemName.toLowerCase();
    const keywords = lowerName.split(/[\s,]+/).filter(w => w.length > 2);

    // Score each part type by how well it matches the item name
    const scored = partTypes.map(pt => {
      const ptName = (pt.name || '').toLowerCase();
      let score = 0;
      keywords.forEach(kw => {
        if (ptName.includes(kw)) score += 10;
      });
      return { ...pt, score };
    });

    // Sort by score (highest first), then alphabetically
    return scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [partTypes, itemName]);

  const handleSearch = async () => {
    if (!registration.trim()) {
      setError('Please enter a vehicle registration');
      return;
    }
    if (!selectedPartType) {
      setError('Please select a part type');
      return;
    }

    setSearchLoading(true);
    setError(null);
    setSearchResults([]);
    setHasSearched(true);

    try {
      const data = await gsfService.searchParts(registration, selectedPartType.id || selectedPartType.name);
      setSearchResults(data.parts || []);

      if (data.parts?.length === 0) {
        setError('No parts found for this vehicle and part type');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to search for parts';
      setError(errorMsg);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectPart = (part) => {
    onSelect({
      sku: part.sku,
      price: part.customerPrice,
      description: part.description,
      brand: part.brand,
      // GSF tracking fields for auto-ordering
      gsf_sku: part.sku,
      gsf_brand: part.brand,
    });
    // Reset and collapse
    setSearchResults([]);
    setSelectedPartType(null);
    setHasSearched(false);
    onToggle();
  };

  // Handle Enter key in registration field
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && registration && selectedPartType) {
      handleSearch();
    }
  };

  // Render stock badge
  const renderStockBadge = (localStock, hubStock) => {
    const totalStock = (localStock || 0) + (hubStock || 0);
    if (totalStock === 0) {
      return <Chip label="Out of Stock" size="small" color="error" />;
    }
    if (localStock > 0) {
      return (
        <Chip
          icon={<InventoryIcon />}
          label={`${localStock} local`}
          size="small"
          color="success"
        />
      );
    }
    return (
      <Chip
        icon={<ShippingIcon />}
        label={`${hubStock} hub`}
        size="small"
        color="warning"
      />
    );
  };

  // Render quality badge
  const renderQualityBadge = (quality) => {
    if (!quality) return null;
    const colorMap = {
      'Gold': 'warning',
      'Silver': 'default',
      'Bronze': 'default',
      'OEM': 'primary',
      'OE': 'primary',
    };
    return (
      <Chip
        label={quality}
        size="small"
        color={colorMap[quality] || 'default'}
        variant="outlined"
      />
    );
  };

  if (disabled) return null;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Toggle Button with GSF Branding */}
      <Button
        fullWidth
        onClick={onToggle}
        sx={{
          justifyContent: 'space-between',
          py: 1,
          px: 2,
          bgcolor: expanded ? '#ed1c24' : '#fafafa',
          color: expanded ? '#fff' : '#333',
          border: '1px solid',
          borderColor: expanded ? '#ed1c24' : '#ddd',
          borderRadius: 1,
          '&:hover': {
            bgcolor: expanded ? '#d91920' : '#f0f0f0',
          },
          textTransform: 'none',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <GSFLogo size={28} />
          <Typography variant="body2" fontWeight="medium">
            Search with GSF Car Parts
          </Typography>
        </Box>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Button>

      {/* Expandable Search Section */}
      <Collapse in={expanded} timeout="auto">
        <Box
          sx={{
            mt: 1,
            p: 2,
            bgcolor: '#fff',
            border: '1px solid #ed1c24',
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(237, 28, 36, 0.15)',
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Search Form */}
          <Box display="flex" gap={2} mb={2} alignItems="flex-start" flexWrap="wrap">
            <TextField
              label="Vehicle Registration"
              value={registration}
              onChange={(e) => setRegistration(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              placeholder="e.g. AB12 CDE"
              size="small"
              sx={{ width: 160 }}
            />

            <Autocomplete
              options={suggestedPartTypes}
              getOptionLabel={(option) => option.name || ''}
              value={selectedPartType}
              onChange={(_, newValue) => setSelectedPartType(newValue)}
              loading={partTypesLoading}
              sx={{ flex: 1, minWidth: 200 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Part Type"
                  placeholder="Search part types..."
                  size="small"
                  helperText={itemName ? `Searching for: ${itemName}` : ''}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id || option.name}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.category && (
                      <Typography variant="caption" color="text.secondary">
                        {option.category}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searchLoading || !registration || !selectedPartType}
              startIcon={searchLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              sx={{
                bgcolor: '#ed1c24',
                '&:hover': { bgcolor: '#d91920' },
                minWidth: 100,
              }}
            >
              Search
            </Button>
          </Box>

          {/* Results Table */}
          {searchLoading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress sx={{ color: '#ed1c24' }} />
            </Box>
          )}

          {!searchLoading && hasSearched && searchResults.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f5f5f5' } }}>
                    <TableCell width={50}><strong>Image</strong></TableCell>
                    <TableCell><strong>Brand</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>SKU</strong></TableCell>
                    <TableCell align="right"><strong>Trade</strong></TableCell>
                    <TableCell><strong>Stock</strong></TableCell>
                    <TableCell><strong>Quality</strong></TableCell>
                    <TableCell align="center" width={50}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((part, index) => (
                    <TableRow
                      key={`${part.sku}-${index}`}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fff5f5' },
                      }}
                      onClick={() => handleSelectPart(part)}
                    >
                      <TableCell>
                        {part.partImageUrl ? (
                          <Box
                            component="img"
                            src={part.partImageUrl}
                            alt={part.description}
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'contain',
                              borderRadius: 0.5,
                              bgcolor: 'grey.100',
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: 'grey.200',
                              borderRadius: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <InventoryIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {part.brand}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 180, fontSize: '0.8rem' }} noWrap>
                          {part.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {part.sku}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          £{part.customerPrice?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {renderStockBadge(part.localStock, part.hubStock)}
                      </TableCell>
                      <TableCell>
                        {renderQualityBadge(part.quality)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          sx={{ color: '#ed1c24' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectPart(part);
                          }}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!searchLoading && hasSearched && searchResults.length === 0 && !error && (
            <Box display="flex" justifyContent="center" py={2}>
              <Typography color="text.secondary" variant="body2">
                No parts found. Try a different part type.
              </Typography>
            </Box>
          )}

          {!hasSearched && (
            <Box display="flex" justifyContent="center" py={2}>
              <Typography color="text.secondary" variant="body2">
                Select a part type and click Search
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default GSFInlineSearch;
